import { BaseHTMLAttributes, ButtonHTMLAttributes, ComponentProps, FC, ReactElement, ReactNode } from 'react';
import styles from './ActionMenu.module.scss';
import Tippy from '@tippyjs/react';
import { joinClassNames } from '../../utils';

type TippyProps = ComponentProps<typeof Tippy>;

type ActionMenuProps = {
  triggerElement: ReactElement;
  children: ReactNode;
} & Omit<TippyProps, 'trigger' | 'hideOnClick' | 'interactive' | 'children'>;

export const ActionMenu: FC<ActionMenuProps> & {
  Item: typeof ActionMenuItem;
  Divider: typeof ActionMenuDivider;
  Content: typeof ActionMenuContent;
} = ({
  animation = 'none',
  placement = 'bottom-end',
  duration = [0, 0],
  offset = [0, 0],
  visible,
  triggerElement,
  className,
  children,
  ...props
}) => {
  return (
    <Tippy
      trigger={visible === undefined ? 'click' : undefined}
      hideOnClick={visible === undefined ? true : undefined}
      interactive={true}
      placement={placement}
      animation={animation}
      duration={duration}
      visible={visible}
      offset={offset}
      content={
        <div className={joinClassNames(styles.outerContainer, className)} role="menu">
          {children}
        </div>
      }
      {...props}
    >
      <div className={styles.triggerContainer}>{triggerElement}</div>
    </Tippy>
  );
};

type ActionMenuItemProps = {
  className?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const ActionMenuItem = ({ className, children, ...props }: ActionMenuItemProps) => (
  <button
    className={joinClassNames(styles.defaultMenuItem, styles.menuItemSelectable, className)}
    role="menuitem"
    {...props}
  >
    {children}
  </button>
);

const ActionMenuDivider = () => (
  <div className={styles.divider}>
    <hr />
  </div>
);

type ActionMenuContentProps = {
  className?: string;
} & BaseHTMLAttributes<HTMLDivElement>;

const ActionMenuContent = ({ className, children, ...props }: ActionMenuContentProps) => (
  <div className={joinClassNames(styles.defaultMenuItem, className)} role="menuitem" {...props}>
    {children}
  </div>
);

ActionMenuItem.displayName = 'ActionMenuItem';
ActionMenu.displayName = 'ActionMenu';
ActionMenu.Item = ActionMenuItem;
ActionMenu.Divider = ActionMenuDivider;
ActionMenu.Content = ActionMenuContent;
